import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import { globalSearchEvent } from 'services/generalServices';
import { groupBy, openLinkInBlank, removeHtmlTags, removePhoneNumberFormatting } from 'utils/utils';
import { useHistory } from 'react-router-dom';
import { contactType } from 'common/dropdownOptions';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { useToggle } from 'hooks/useToggle';
import { getFileExtension, getFileType } from 'utils/filesUtils';
import axios from 'axios';
import GlobalTooltip from 'components/GlobalTooltip';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { MultiSelect } from 'primereact/multiselect';
import { GLOBAL_SEARCH_FILTER_OPTIONS, GLOBAL_SEARCH_FILTER_TO_MODULES_MAP } from 'utils/searchConstants';

export default function GlobalSearch() {
  const { checkSubPermission } = useUserDetailsContext();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const wrapperRef = useRef(null);
  const showContentRef = useRef(null);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [showContent, toggleContent] = useToggle(false);
  const cancelTokenSource = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  const filterOptions = GLOBAL_SEARCH_FILTER_OPTIONS;

  // Map UI filter to API modules string
  const filterToModulesMap = GLOBAL_SEARCH_FILTER_TO_MODULES_MAP;

  function getModulesParam(filtersArg = selectedFilters) {
    if (!filtersArg || filtersArg.length === 0) return '';
    return (filtersArg || [])
      .map((f) => filterToModulesMap[f])
      .filter(Boolean)
      .join(',');
  }

  const shouldShow = (key) => !selectedFilters?.length || selectedFilters?.includes(key);

  function findType(item) {
    return getFileType(getFileExtension(item?.file_name));
  }

  const fetchData = async (query, filtersArg) => {
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel();
    }
    cancelTokenSource.current = axios.CancelToken.source();

    setLoading(true);
    try {
      const modules = getModulesParam(filtersArg);
      const res = await globalSearchEvent(
        query,
        showContentRef.current,
        false,
        true,
        { cancelToken: cancelTokenSource.current.token },
        modules
      );
      if (res.data && !showContentRef.current) {
        const rawList = Array.isArray(res?.data) ? res?.data : res?.data?.results || [];
        const grouped = groupBy(
          rawList?.map((v) => {
            const source = v?._source || v;
            return {
              ...source,
              res_type: source?.res_type?.includes('file_system_entry') ? 'file_system_entry' : source?.res_type,
              // Map object_key to file_path for file entries to ensure proper redirection
              file_path: source?.res_type?.includes('file_system_entry') ? source?.file_path || source?.object_key : source?.file_path,
            };
          }),
          'res_type'
        );
        setResults(grouped);
      } else if (res.data && showContentRef.current) {
        // Support both old and new response shapes:
        // Old: res.data.documents.buckets
        // New: res.data.results.documents.buckets
        const documentsRoot = res?.data?.documents || res?.data?.results?.documents;
        const buckets = documentsRoot?.buckets || [];
        const data = buckets.map((v) => {
          const source = v?.top_hit?.hits?.hits?.[0]?._source || {};
          return {
            ...source,
            file_path: source?.object_key || source?.file_path,
            object_id: source?.document_id || source?.file_id || source?.object_id,
            type: findType(source),
          };
        });
        setResults(data);
      }
      setLoading(false);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Previous request canceled:', error.message);
      } else {
        console.error(error);
        setLoading(false);
      }
    }
  };

  const delayedSearch = useCallback(
    debounce((query, filtersArg) => {
      if (query !== '') {
        fetchData(removePhoneNumberFormatting(query), filtersArg);
      } else {
        if (cancelTokenSource.current) {
          cancelTokenSource.current.cancel();
          cancelTokenSource.current = null;
        }
        setResults({});
        setLoading(false);
      }
    }, 500),
    [selectedFilters]
  );

  useEffect(() => {
    setLoading(true);
    setResults({});
    delayedSearch(query, selectedFilters);
  }, [query, selectedFilters]); //eslint-disable-line

  useEffect(() => {
    showContentRef.current = showContent;
  }, [showContent]); //eslint-disable-line

  // Detect mobile viewport to ensure module dropdown shows with search bar
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 991.98px)');
    const update = () => setIsMobile(Boolean(mq.matches));
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
    } else if (mq.addListener) {
      mq.addListener(update);
    }
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener('change', update);
      } else if (mq.removeListener) {
        mq.removeListener(update);
      }
    };
  }, []);

  const redirectToPage = (pathname, state, search) => {
    if (!openLinkInBlank(pathname, state)) {
      history?.replace({
        pathname,
        state,
        search,
      });
    }
  };

  const cleanSk = (sk) => {
    if (!sk) return sk;
    const parts = sk.split('#');
    if (parts.length >= 3 && parts[1] === 'firm_user') {
      return parts.slice(1).join('#');
    }
    return sk;
  };

  const goToDetails = (data) => {
    if (data?.res_type === 'case') {
      if (data?.is_lead) {
        return `/leads/${data?.case_id}`;
      } else {
        return `/cases/${data?.case_id}`;
      }
    } else if (data?.res_type === 'message') {
      return `/messages/${data?.message_thread_id}`;
    } else if (data?.res_type === 'message-reply') {
      return `/messages/${data?.pk?.split('#')?.[1]}`;
    } else if (data?.res_type === 'text_message_thread' || data?.res_type === 'text_message_reply') {
      const contactId = data?.contact_id;
      const phoneNumber = data?.to || data?.To;

      if (contactId && phoneNumber) {
        return `/text-message/${contactId}/${phoneNumber}`;
      } else {
        return '/text-messages';
      }
    } else if (data?.res_type === 'file_system_entry' || data?.res_type?.includes('file_system_entry') || showContentRef.current) {
      localStorage.setItem('viewFile', JSON.stringify(data));

      let fileId = data?.object_id || data?.file_id || data?.document_id;
      if (!fileId && data?.sk) {
        const skParts = data.sk.split('#');
        fileId = skParts.length > 1 ? skParts[1] : null;
      }

      // Ensure file has object_id set for FileManagerView to find it
      const fileData = {
        ...data,
        object_id: fileId,
      };

      // Add file_id query parameter for FileManagerView
      const searchParam = fileId ? `?file_id=${fileId}` : '';

      if (data?.case_id && data?.case_id !== 'undefined') {
        return {
          pathname: `/${data?.is_lead ? 'leads' : 'cases'}/${data?.case_id}/files`,
          search: searchParam,
          state: { file: fileData },
        };
      } else {
        return {
          pathname: `/file-manager`,
          search: searchParam,
          state: { file: fileData },
        };
      }
    } else if (data?.res_type === 'event') {
      return {
        pathname: '/calendar',
        search: `?id=${data?.pk?.split('#')?.[1]}`,
      };
    } else if (data?.res_type === 'user') {
      if (data?.access_level && data?.access_level !== 'client') {
        return `/settings/team-management/${data?.user_id}`;
      } else if (contactType.includes(data?.contact_type || !data?.access_level || data?.access_level === 'client')) {
        return `/contacts/${data?.contact_id}`;
      }
    } else if (data?.res_type === 'task') {
      let taskId = data?.task_id;
      if (!taskId && data?.sk) {
        const skParts = data.sk.split('#');
        taskId = skParts.length > 1 ? skParts[1] : null;
      }

      const searchParam = taskId ? `?id=${taskId}` : '';
      return {
        pathname: '/tasks',
        search: searchParam,
        state: { viewTask: data },
      };
    } else if (data?.res_type === 'invoice') {
      let invoiceId = null;
      if (data?.sk && data.sk.includes('#')) {
        const skParts = data.sk.split('#');
        invoiceId = skParts[skParts.length - 1];
      }

      const invoiceSk = invoiceId ? `invoice#${invoiceId}` : null;
      const searchParam = invoiceSk ? `?invoice_sk=${encodeURIComponent(invoiceSk)}` : '';

      return {
        pathname: `/cases/${data?.case_id ? data?.case_id : data?.invoice?.case_id}/billing`,
        search: searchParam,
      };
    } else if (data?.res_type === 'payment_request') {
      let paymentRequestId = null;
      if (data?.sk && data.sk.includes('#')) {
        const skParts = data.sk.split('#');
        paymentRequestId = skParts[skParts.length - 1];
      }

      const paymentRequestSk = paymentRequestId ? `payment_request#${paymentRequestId}` : null;
      const searchParam = paymentRequestSk ? `?payment_request_sk=${encodeURIComponent(paymentRequestSk)}` : '';

      return {
        pathname: `/cases/${data?.case_id}/billing`,
        search: searchParam,
      };
    } else if (data?.res_type === 'case-note') {
      let caseNoteId = data?.case_note_id;
      if (!caseNoteId && data?.sk) {
        const skParts = data.sk.split('#');
        caseNoteId = skParts.length > 1 ? skParts[1] : null;
      }

      const pathname = caseNoteId ? `/cases/${data?.case_id}/notes/${caseNoteId}` : `/cases/${data?.case_id}/notes`;

      return {
        pathname,
        state: { case_note: data },
      };
    }
    return '/';
  };

  //close search when clicked outside
  useEffect(() => {
    function handleClickOutside(event) {
      const target = event.target;
      // Ignore clicks inside PrimeReact overlay panels (e.g., Dropdown panel)
      if (
        target?.closest?.('.p-dropdown-panel') ||
        target?.closest?.('.p-dropdown') ||
        target?.closest?.('.p-multiselect-panel') ||
        target?.closest?.('.p-multiselect') ||
        target?.closest?.('.p-overlaypanel')
      ) {
        return;
      }
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        close();
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  const close = () => {
    setLoading(false);
    setResults({});
    setIsOpen(false);
    setQuery('');
  };
  const onSearchKeyDown = (e) => {
    let code = e.keyCode ? e.keyCode : e.which;
    if (code === 13) {
      delayedSearch(query, selectedFilters);
    }
  };

  const loader = () => {
    return (
      <div className="overlay searchbutton">
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '1em' }}></i>
      </div>
    );
  };

  return (
    <div className="search-container" ref={wrapperRef}>
      {(isOpen || isMobile) && (
        <span className="search-filter me-0 d-inline-flex align-items-center">
          <MultiSelect
            value={selectedFilters}
            options={filterOptions}
            onChange={(e) => {
              setSelectedFilters(e.value || []);
              if (query) {
                setLoading(true);
                setResults({});
                fetchData(removePhoneNumberFormatting(query), e.value || []);
              }
            }}
            optionLabel="label"
            optionValue="value"
            appendTo={typeof document !== 'undefined' ? document.body : null}
            placeholder="All"
            display="chip"
            showClear
            filter
            className="w-100"
            style={{ width: 250 }}
          />
        </span>
      )}
      <span className="p-input-icon-left autowidth d-inline-flex">
        <TextSnippetWrapper
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'flex',
            paddingRight: '60px',
          }}
          type="text"
          className={
            isOpen
              ? 'open search expandright input-style with-filter'
              : isMobile
              ? 'search expandright input-style with-filter'
              : 'search expandright input-style'
          }
          id="searchright"
          autoComplete="off"
          placeholder="Search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value || '');
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={onSearchKeyDown}
        />
      </span>
      {checkSubPermission('full_text_search') && isOpen && (
        <span
          role="button"
          onClick={() => {
            if (isOpen && query) {
              setLoading(true);
            }
            setResults({});
            toggleContent();
            delayedSearch(query, selectedFilters);
          }}
        >
          <GlobalTooltip tooltip="Search Content Data" className="me-1 fa-lg">
            <i className={`icon-files fa-lg me-lg-0 me-4 ${showContent ? 'highlight' : ''}`} />
          </GlobalTooltip>
        </span>
      )}
      {loading ? (
        loader()
      ) : (
        <label
          className="button searchbutton pointer"
          htmlFor="searchright"
          onClick={() => {
            setIsOpen(true);
            if (isOpen && query) {
              delayedSearch(query, selectedFilters);
            }
          }}
        >
          <GlobalTooltip tooltip="Search">
            <i className="icon-search fa-lg" />
          </GlobalTooltip>
        </label>
      )}
      {Object.keys(results || {})?.length > 0 && (
        <div className={isOpen ? 'result open' : 'result'}>
          {!showContentRef.current ? (
            <div className="result-list">
              {shouldShow('contacts') &&
                Boolean(
                  results?.user?.filter(
                    (v) => (v.access_level && v.access_level === 'client') || !v.access_level || contactType.includes(v?.contact_type)
                  ).length > 0
                ) && (
                  <div className="list">
                    <h1 className="title">Contacts</h1>
                    {results?.user
                      ?.filter(
                        (v) => (v.access_level && v.access_level === 'client') || !v.access_level || contactType.includes(v?.contact_type)
                      )
                      .map((v, i) => (
                        <li key={i}>
                          <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                            <h1 className="sub-title text-primary-dark">
                              {v.contact_type === 'person' ? `${v?.first_name} ${v?.last_name}` : v?.title}
                            </h1>
                          </Link>
                        </li>
                      ))}
                  </div>
                )}

              {shouldShow('cases') && Boolean(results?.case?.filter((v) => !Boolean(v?.is_lead)).length > 0) && (
                <div className="list">
                  <h1 className="title">Cases</h1>
                  {results?.case
                    ?.filter((v) => !Boolean(v?.is_lead))
                    .map((v, i) => (
                      <li key={i}>
                        <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                          <h1 className="sub-title text-primary-dark">{v?.case_description?.case_name}</h1>
                        </Link>
                      </li>
                    ))}
                </div>
              )}

              {shouldShow('leads') && Boolean(results?.case?.filter((v) => v?.is_lead)?.length > 0) && (
                <div className="list">
                  <h1 className="title">Leads</h1>
                  {results?.case
                    ?.filter((v) => v?.is_lead)
                    .map((v, i) => (
                      <li key={i}>
                        <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                          <h1 className="sub-title text-primary-dark">{v?.case_description?.case_name}</h1>
                        </Link>
                      </li>
                    ))}
                </div>
              )}

              {shouldShow('files') && Boolean(results?.file_system_entry?.length > 0) && (
                <div className="list">
                  <h1 className="title">Files</h1>
                  {results?.file_system_entry?.map((v, i) => (
                    <li key={i}>
                      <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                        <h1 className="sub-title text-primary-dark">{v?.display_name}</h1>
                      </Link>
                    </li>
                  ))}
                </div>
              )}

              {shouldShow('firm_users') &&
                Boolean(results?.user?.filter((v) => v.access_level && v.access_level !== 'client').length > 0) && (
                  <div className="list">
                    <h1 className="title">Firm Users</h1>
                    {results?.user
                      ?.filter((v) => v.access_level && v.access_level !== 'client')
                      .map((v, i) => (
                        <li key={i}>
                          <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                            <h1 className="sub-title text-primary-dark">
                              {v?.first_name} {v?.last_name}
                            </h1>
                          </Link>
                        </li>
                      ))}
                  </div>
                )}

              {shouldShow('events') && Boolean(results?.event?.length > 0) && (
                <div className="list">
                  <h1 className="title">Events</h1>
                  {results?.event?.map((v, i) => (
                    <li key={i}>
                      <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                        <h1 className="sub-title text-primary-dark">
                          {v?.case_name && v?.case_name !== '-' ? v?.case_name + ': ' : ''} {v?.title}
                        </h1>
                      </Link>
                    </li>
                  ))}
                </div>
              )}

              {shouldShow('messages') && (Boolean(results?.message?.length > 0) || Boolean(results?.['message-reply']?.length > 0)) && (
                <div className="list">
                  <h1 className="title">Messages</h1>
                  {results?.message?.map((v, i) => (
                    <li key={i}>
                      <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                        <h1 className="sub-title text-primary-dark">{v?.subject}</h1>
                      </Link>
                    </li>
                  ))}
                  {results?.['message-reply']?.map((v, i) => (
                    <li key={i}>
                      <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                        <h1 className="sub-title text-primary-dark">{removeHtmlTags(v?.message)}</h1>
                      </Link>
                    </li>
                  ))}
                </div>
              )}
              {shouldShow('text_messages') &&
                (Boolean(results?.text_message_thread?.length > 0) || Boolean(results?.['text_message_reply']?.length > 0)) && (
                  <div className="list">
                    <h1 className="title">Text Messages</h1>
                    {results?.text_message_thread?.map((v, i) => (
                      <li key={i}>
                        <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                          <h1 className="sub-title text-primary-dark">{v?.body ? v?.body : v?.title}</h1>
                        </Link>
                      </li>
                    ))}
                    {results?.text_message_reply?.map((v, i) => (
                      <li key={i}>
                        <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                          <h1 className="sub-title text-primary-dark">{v?.body ? v?.body : v?.title}</h1>
                        </Link>
                      </li>
                    ))}
                  </div>
                )}

              {shouldShow('task') && Boolean(results?.task?.length > 0) && (
                <div className="list">
                  <h1 className="title">Task</h1>
                  {results?.task?.map((v, i) => (
                    <li key={i}>
                      <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                        <h1 className="sub-title text-primary-dark ">
                          {v?.case_name ? v?.case_name + ': ' : ''} {v?.task_name}
                        </h1>
                      </Link>
                    </li>
                  ))}
                </div>
              )}
              {shouldShow('payment_request') && Boolean(results?.payment_request?.length > 0) && (
                <div className="list">
                  <h1 className="title">Payment Request</h1>
                  {results?.payment_request?.map((v, i) => (
                    <li key={i}>
                      <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                        <h1 className="sub-title text-primary-dark">
                          {v?.case_name ? v?.case_name + ': ' : ''} {v?.payment_request_number}
                        </h1>
                      </Link>
                    </li>
                  ))}
                </div>
              )}
              {shouldShow('invoice') && Boolean(results?.invoice?.length > 0) && (
                <div className="list">
                  <h1 className="title">Invoice</h1>
                  {results?.invoice?.map((v, i) => (
                    <li key={i}>
                      <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                        <h1 className="sub-title text-primary-dark">
                          {v?.invoice?.case_name ? v?.invoice?.case_name + ': ' : ''} {v?.invoice_number}
                        </h1>
                      </Link>
                    </li>
                  ))}
                </div>
              )}
              {shouldShow('case_note') && Boolean(results?.['case-note']?.length > 0) && (
                <div className="list">
                  <h1 className="title">Case Note</h1>
                  {results?.['case-note']?.map((v, i) => (
                    <li key={i}>
                      <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                        <h1 className="sub-title text-primary-dark">{v?.title && v?.title}</h1>
                      </Link>
                    </li>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="result-list">
              {shouldShow('files') && Boolean(results?.length > 0) && (
                <div className="list">
                  <h1 className="title">Files</h1>
                  {results?.map((v, i) => (
                    <li key={i}>
                      <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                        <h1 className="sub-title text-primary-dark">{decodeURIComponent(v?.file_name)}</h1>
                      </Link>
                    </li>
                  ))}
                </div>
              )}
            </div>
          )}
          <hr className="my-1" />
          <Link
            to={`/search?search=${query}${showContentRef.current ? `&showContent=true` : ''}${
              selectedFilters?.length ? `&filter=${selectedFilters.join(',')}` : ''
            }`}
            className="redirection-link d-flex justify-content-center"
            onClick={() => setIsOpen(false)}
          >
            View All
          </Link>
        </div>
      )}
      {Object.keys(results || {})?.length === 0 && query?.length > 0 && loading === false && (
        <div className={isOpen ? 'result open' : 'result'}>
          <div className="list p-0 m-0">
            <h1 className="sub-title m-0">No Data Found</h1>
          </div>
        </div>
      )}
    </div>
  );
}
